import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import useCrmConfig from "@/hooks/useCrmConfig";
import { BookUser, Star } from "lucide-react";

/**
 * Private "in your contacts" panel shown to the VIEWER on a member's profile.
 * The profile owner never sees this, and it only ever answers about the viewer's own
 * records — it can never reveal who else saved this member.
 */
export default function ProfileCrmPanel({ profileUserId, currentUserId }) {
  const { visible } = useCrmConfig();
  const [contact, setContact] = useState(null);

  useEffect(() => {
    if (!visible || !profileUserId || profileUserId === currentUserId) return;
    api.get(`/crm/by-user/${profileUserId}`)
      .then((r) => setContact(r.data.contact))
      .catch(() => setContact(null));
  }, [visible, profileUserId, currentUserId]);

  if (!visible || !contact) return null;

  return (
    <aside data-testid="profile-crm-panel"
      className="rounded-xl border border-border bg-card p-4">
      <div className="flex items-center gap-2 text-sm font-[640]">
        <BookUser className="h-4 w-4 text-primary" /> In your contacts
        {contact.favorite && <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-500" />}
      </div>
      <p className="mt-2 text-sm text-muted-foreground">
        {contact.how_we_met || "Saved in your private CRM."}
      </p>
      <dl className="mt-3 flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
        <div><dt className="inline font-[600]">Notes:</dt> <dd className="inline">{contact.note_count}</dd></div>
        {contact.cadence_days > 0 && (
          <div><dt className="inline font-[600]">Cadence:</dt> <dd className="inline">{contact.cadence_days}d</dd></div>
        )}
        {contact.tags?.length > 0 && (
          <div><dt className="inline font-[600]">Tags:</dt>{" "}
            <dd className="inline">{contact.tags.map((t) => t.label).join(", ")}</dd></div>
        )}
      </dl>
      <Link to={`/dashboard/contacts/${contact.contact_id}`} data-testid="profile-crm-open"
        className="mt-3 inline-block text-sm font-[560] text-primary hover:underline">
        Open private contact →
      </Link>
      <p className="mt-2 text-[11px] text-muted-foreground">Only you can see this.</p>
    </aside>
  );
}
